import { useEffect, useRef, useState } from 'react';
import { LinkDialog } from './MailingDialogs';

const EDIT_STYLE_ID = 'p1-visual-editor-style';
const P_STYLE = 'margin:0 0 20px;font-size:16px;line-height:1.6;color:#111111;';
const LI_STYLE = 'margin:0 0 6px;font-size:16px;line-height:1.6;color:#111111;';
const LIST_STYLE = 'margin:0 0 20px;padding-left:22px;';
const H2_STYLE = 'margin:0 0 14px;font-size:22px;line-height:1.3;color:#111111;font-weight:700;';
const A_STYLE = 'color:#4f46e5;text-decoration:underline;';

function editableRoots(doc: Document): HTMLElement[] {
  const marked = [...doc.querySelectorAll<HTMLElement>('[data-editable]')];
  return marked.length ? marked : [doc.body];
}

function normalize(root: HTMLElement) {
  root.querySelectorAll('div:not([data-editable])').forEach((d) => {
    if (d.closest('table')) return;
    const p = d.ownerDocument.createElement('p');
    p.innerHTML = d.innerHTML;
    d.replaceWith(p);
  });
  root.querySelectorAll<HTMLElement>('p').forEach((el) => { if (!el.getAttribute('style')) el.setAttribute('style', P_STYLE); });
  root.querySelectorAll<HTMLElement>('ul,ol').forEach((el) => { if (!el.getAttribute('style')) el.setAttribute('style', LIST_STYLE); });
  root.querySelectorAll<HTMLElement>('li').forEach((el) => { if (!el.getAttribute('style')) el.setAttribute('style', LI_STYLE); });
  root.querySelectorAll<HTMLElement>('h2').forEach((el) => { if (!el.getAttribute('style')) el.setAttribute('style', H2_STYLE); });
  root.querySelectorAll<HTMLElement>('a').forEach((el) => { if (!el.getAttribute('style')) el.setAttribute('style', A_STYLE); });
  root.querySelectorAll('font').forEach((el) => el.replaceWith(...el.childNodes));
}

function serialize(doc: Document): string {
  const clone = doc.documentElement.cloneNode(true) as HTMLElement;
  clone.querySelector(`#${EDIT_STYLE_ID}`)?.remove();
  clone.querySelectorAll('[contenteditable]').forEach((el) => el.removeAttribute('contenteditable'));
  clone.querySelectorAll('[data-editable]').forEach((el) => normalize(el as HTMLElement));
  if (!clone.querySelector('[data-editable]')) {
    const body = clone.querySelector('body');
    if (body) body.removeAttribute('contenteditable');
  }
  return `<!doctype html>\n${clone.outerHTML}`;
}

export function EmailVisualEditor({ html, onChange }: { html: string; onChange: (html: string) => void }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const emitted = useRef<string | null>(null);
  const [srcDoc, setSrcDoc] = useState(html);
  const [height, setHeight] = useState(520);
  const [whole, setWhole] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const savedRange = useRef<Range | null>(null);

  useEffect(() => {
    if (html !== emitted.current) setSrcDoc(html);
  }, [html]);

  function doc(): Document | null {
    return frame.current?.contentDocument ?? null;
  }

  function resize() {
    const d = doc();
    if (d?.documentElement) setHeight(Math.max(420, d.documentElement.scrollHeight + 4));
  }

  function emit() {
    const d = doc();
    if (!d) return;
    const out = serialize(d);
    emitted.current = out;
    onChange(out);
    resize();
  }

  function onLoad() {
    const d = doc();
    if (!d) return;
    const style = d.createElement('style');
    style.id = EDIT_STYLE_ID;
    style.textContent = '[contenteditable="true"]{outline:1px dashed #b9b2f5;outline-offset:6px;border-radius:4px;min-height:40px}[contenteditable="true"]:focus{outline:2px solid #4f46e5}';
    d.head.appendChild(style);
    const roots = editableRoots(d);
    setWhole(roots.length === 1 && roots[0] === d.body);
    d.execCommand('defaultParagraphSeparator', false, 'p');
    roots.forEach((r) => {
      r.setAttribute('contenteditable', 'true');
      r.addEventListener('input', emit);
      r.addEventListener('paste', (e: ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData?.getData('text/plain') ?? '';
        d.execCommand('insertText', false, text);
      });
    });
    d.addEventListener('click', (e) => {
      const a = (e.target as HTMLElement).closest('a');
      if (a) e.preventDefault();
    });
    resize();
  }

  function cmd(command: string, value?: string) {
    const d = doc();
    if (!d) return;
    const root = editableRoots(d)[0];
    const sel = d.getSelection();
    if (!sel || !sel.rangeCount || !root?.contains(sel.anchorNode)) root?.focus();
    d.execCommand(command, false, value);
    emit();
  }

  function link() {
    const sel = doc()?.getSelection();
    savedRange.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    setLinkOpen(true);
  }

  function applyLink(url: string) {
    setLinkOpen(false);
    const d = doc();
    const r = savedRange.current;
    if (d && r) {
      const sel = d.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(r);
    }
    cmd('createLink', url);
  }


  return (
    <div className="email-visual-editor">
      <div className="email-visual-toolbar">
        <button type="button" title="Tučně" onMouseDown={(e) => { e.preventDefault(); cmd('bold'); }}><strong>B</strong></button>
        <button type="button" title="Kurzíva" onMouseDown={(e) => { e.preventDefault(); cmd('italic'); }}><em>I</em></button>
        <button type="button" title="Podtržení" onMouseDown={(e) => { e.preventDefault(); cmd('underline'); }}><u>U</u></button>
        <span className="sep" />
        <button type="button" title="Nadpis" onMouseDown={(e) => { e.preventDefault(); cmd('formatBlock', 'h2'); }}>Nadpis</button>
        <button type="button" title="Odstavec" onMouseDown={(e) => { e.preventDefault(); cmd('formatBlock', 'p'); }}>Odstavec</button>
        <button type="button" title="Odrážky" onMouseDown={(e) => { e.preventDefault(); cmd('insertUnorderedList'); }}>• Odrážky</button>
        <button type="button" title="Číslovaný seznam" onMouseDown={(e) => { e.preventDefault(); cmd('insertOrderedList'); }}>1. Seznam</button>
        <span className="sep" />
        <button type="button" title="Vložit odkaz" onMouseDown={(e) => { e.preventDefault(); link(); }}>Odkaz</button>
        <button type="button" title="Zrušit odkaz" onMouseDown={(e) => { e.preventDefault(); cmd('unlink'); }}>Zrušit odkaz</button>
        <button type="button" title="Vložit oslovení" onMouseDown={(e) => { e.preventDefault(); cmd('insertText', '[% shared.VOCATIVE %]'); }}>Oslovení</button>
        <button type="button" title="Zrušit formátování" onMouseDown={(e) => { e.preventDefault(); cmd('removeFormat'); }}>Vyčistit</button>
      </div>
      {whole && <div className="email-visual-note">Šablona nemá vyznačenou oblast obsahu, upravovat jde celý e-mail. Hlídej oslovení a odhlašovací odkaz.</div>}
      {linkOpen && <LinkDialog onSubmit={applyLink} onClose={() => setLinkOpen(false)} />}
      <iframe ref={frame} title="Vizuální editor e-mailu" sandbox="allow-same-origin" srcDoc={srcDoc} onLoad={onLoad} style={{ height }} />
    </div>
  );
}
